// Theme Utility Functions
// Helper functions to apply theme and content configurations

import { theme, componentThemes, getColor, getTypography, layout } from '../config/theme';
import { content, getContent } from '../config/content';

// CSS class generators
export const generateColorClasses = (colorPath: string, prefix: string = '') => {
  const color = getColor(colorPath);
  return {
    backgroundColor: prefix ? `${prefix}-bg-[${color}]` : `bg-[${color}]`,
    textColor: prefix ? `${prefix}-text-[${color}]` : `text-[${color}]`,
    borderColor: prefix ? `${prefix}-border-[${color}]` : `border-[${color}]`,
  };
};

export const generateTypographyClasses = (path: string, prefix: string = '') => {
  const value = getTypography(path);
  return prefix ? `${prefix}-${value}` : value;
};

// Component theme helpers
export const getNavbarTheme = () => componentThemes.navbar;
export const getButtonTheme = (variant: 'primary' | 'secondary' | 'success' | 'danger') => 
  componentThemes.buttons[variant];
export const getCardTheme = () => componentThemes.cards;
export const getFormTheme = () => componentThemes.forms;
export const getDatePickerTheme = () => componentThemes.datePicker;

// Content helpers
export const getCompanyInfo = () => content.company;
export const getNavigationContent = () => content.navigation;
export const getHomeContent = () => content.home;
export const getRentalsContent = () => content.rentals;
export const getProductDetailsContent = () => content.productDetails;
export const getBookingContent = () => content.booking;
export const getContactContent = () => content.contact;
export const getAdminContent = () => content.admin;

// Dynamic content with parameters
export const formatContent = (path: string, params?: Record<string, string | number>) => {
  const stringParams = params ? 
    Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)])) : 
    undefined;
  return getContent(path, stringParams);
};

// Theme-aware component props
export interface ThemeComponentProps {
  className?: string;
  variant?: string;
  size?: string;
  disabled?: boolean;
}

// Responsive utility
export const responsiveClasses = (classes: Record<string, string>) => {
  return Object.entries(classes)
    .map(([breakpoint, className]) => {
      if (breakpoint === 'base') return className;
      return `${breakpoint}:${className}`;
    })
    .join(' ');
};

// Animation utilities
export const animations = {
  transitions: {
    fast: 'transition-colors duration-150',
    base: 'transition-colors duration-200',
    slow: 'transition-colors duration-300',
  },
  transforms: {
    hover: 'hover:scale-105',
    active: 'active:scale-95',
  },
  spin: 'animate-spin',
};

export const getTransitionClasses = (speed: 'fast' | 'base' | 'slow' = 'base') => {
  return animations.transitions[speed];
};

export const getTransformClasses = (transform: 'hover' | 'active' = 'hover') => {
  return animations.transforms[transform];
};

// Color utilities
export const getColorValue = (colorPath: string) => getColor(colorPath);
export const getSemanticColor = (type: 'success' | 'warning' | 'error' | 'info') => 
  theme.colors.semantic[type];

// Spacing utilities
export const getSpacing = (size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl') => 
  theme.spacing[size];

// Border radius utilities
export const getBorderRadius = (size: 'none' | 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl' | 'full') => 
  theme.borderRadius[size];

// Shadow utilities
export const getShadow = (size: 'sm' | 'base' | 'md' | 'lg' | 'xl') => 
  theme.shadows[size];

// Conditional class utilities
export const conditionalClass = (condition: boolean, className: string) => 
  condition ? className : '';

export const conditionalClasses = (conditions: Record<string, boolean>) => 
  Object.entries(conditions)
    .filter(([_, condition]) => condition)
    .map(([className]) => className)
    .join(' ');

// Merge classes utility
export const mergeClasses = (...classes: (string | undefined | null | false)[]) => 
  classes.filter(Boolean).join(' ');

// Theme-aware styles for inline styles
export const getThemeStyles = () => ({
  fontFamily: theme.typography.fontFamily.primary,
  colors: theme.colors,
  spacing: theme.spacing,
  borderRadius: theme.borderRadius,
  shadows: theme.shadows,
});

// Content-aware styles
export const getContentStyles = () => ({
  company: content.company,
  navigation: content.navigation,
  pages: {
    home: content.home,
    rentals: content.rentals,
    productDetails: content.productDetails,
    booking: content.booking,
    contact: content.contact,
  },
  admin: content.admin,
  common: content.messages,
});

// Export all theme and content for easy access
export { theme, content };
export default { theme, content, componentThemes, layout, animations };
